import { createAction, type ThunkAction, type AnyAction } from '@reduxjs/toolkit';
import { saveToLocalStorage } from '@/lib/helpers';
import type { RootState } from '@/app/store';
import type { AppDispatch } from '@/app/store';
import { api } from '@/lib/trpc/client';
import type { SalesOrderDeliveryTrackingItem } from '@/server/sales-orders/sales-order-delivery-tracking';
import { showPrimarySnackbar } from '@/lib/atoms/notifications';

export interface SalesOrdersDeliveryTrackingFilters {
  season: string;
  quarters: string[];
  campaign: string;
  brands: string[];
  productType: string;
  onlyChangedProducts: boolean;
  shopId: number | undefined;
}

interface SearchFilters {
  quarters?: string[];
  brands?: string[];
  shopId: number | undefined;
  season: string;
  productType: string;
  campaign: string;
}

export const salesOrdersDeliveryTrackingRequestStarted = createAction(
  'sales-orders-delivery-tracking/request/started'
);
export const salesOrdersDeliveryTrackingRequestFailed = createAction<string>(
  'sales-orders-delivery-tracking/delivery-tracking/failed'
);
export const setSalesOrdersDeliveryTrackingItems = createAction<SalesOrderDeliveryTrackingItem[]>(
  'sales-orders-delivery-tracking/items/set'
);
export const setSalesOrdersDeliveryTrackingActiveFilters =
  createAction<SalesOrdersDeliveryTrackingFilters>(
    'sales-orders-delivery-tracking/active-filters/save'
  );

/**
 * Update active filters
 */
export const _setSalesOrdersDeliveryTrackingActiveFilters =
  (
    filterUpdate: Partial<SalesOrdersDeliveryTrackingFilters> | null
  ): ThunkAction<void, RootState, undefined, AnyAction> =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const salesOrdersDeliveryTrackingState = state.salesOrdersDeliveryTracking;
    const { activeFilters } = salesOrdersDeliveryTrackingState;

    let newFilters: SalesOrdersDeliveryTrackingFilters;

    if (filterUpdate === null) {
      const productPageDataState = state.productPageData;
      const { productPageData } = productPageDataState;

      newFilters = {
        season: productPageData?.assortmentSeason || '',
        quarters: [],
        campaign: '',
        brands: [],
        productType: '',
        onlyChangedProducts: false,
        shopId: undefined,
      };
    } else {
      newFilters = {
        ...activeFilters,
        ...filterUpdate,
      };
    }

    saveToLocalStorage('salesOrdersDeliveryTrackingActiveFilters', newFilters);
    dispatch(setSalesOrdersDeliveryTrackingActiveFilters(newFilters));
  };

/**
 * Get delivery tracking items
 */
export const getDeliveryTrackingItems =
  (searchFilters: SearchFilters) => (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(salesOrdersDeliveryTrackingRequestStarted());

    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;

    if (!(searchFilters.shopId && searchFilters.season)) {
      dispatch(salesOrdersDeliveryTrackingRequestFailed('Missing required parameters'));
      return Promise.resolve();
    }

    return api.salesOrders.getDeliveryTrackingList
      .query({
        quarters: searchFilters.quarters || [],
        brands: searchFilters.brands || [],
        shopId: searchFilters.shopId,
        season: searchFilters.season,
        productType: searchFilters.productType,
        campaign: searchFilters.campaign,
        chain: activeChain,
      })
      .then(
        (data) => {
          dispatch(setSalesOrdersDeliveryTrackingItems(data));
        },
        ({ message }) => {
          dispatch(salesOrdersDeliveryTrackingRequestFailed(message));
          showPrimarySnackbar({
            message: `Failed to get delivery tracking items: ${message}`,
          });
        }
      );
  };
